// The consent wording a club agrees to when uploading photos and videos —
// as DATA, versioned (Scott, 2026-10-06, Q2).
//
// ⚠️⚠️ THE WORDING BELOW IS A PLACEHOLDER. Scott supplies the real wording
// (checked) before launch. Swapping it in is a DATA change to this file:
//
//   1. add a NEW entry to CONSENT_WORDINGS under a new version string
//      (e.g. '2026-11-v2') with the real text and placeholder: false;
//   2. point CURRENT_CONSENT_VERSION at it;
//   3. LEAVE THE OLD ENTRY IN PLACE.
//
// No component, route or schema changes: the upload screen renders
// `currentConsent().text`, sends back `currentConsent().version`, and the
// server stores the version it was SENT — once it has checked that version is
// the current one.
//
// ⚠️⚠️ WHY THE VERSION THE PERSON SAW, NOT "WHATEVER IS CURRENT". A tab opened
// before a wording change still shows the old words. If the server stamped the
// current version, that person would be recorded as agreeing to text they
// never read. So the client sends the version it displayed, and the server
// REFUSES (400, "reload") any version that is not current, rather than either
// accepting old words for a new upload or silently upgrading the record.
//
// ⚠️ Old entries stay forever: every stored asset names a version, and that
// version must still resolve to the words its uploader was shown.

export type ConsentWording = {
  version: string;
  text: string;
  /** true until Scott's real wording replaces it — the launch check reads this. */
  placeholder: boolean;
};

export const CONSENT_WORDINGS: Readonly<Record<string, ConsentWording>> = {
  '2026-10-v1': {
    version: '2026-10-v1',
    text:
      'I confirm I have permission from everyone shown in these photos and videos ' +
      'for them to be used in marketing for this business.',
    placeholder: true,
  },
};

export const CURRENT_CONSENT_VERSION = '2026-10-v1';

export function currentConsent(): ConsentWording {
  const w = CONSENT_WORDINGS[CURRENT_CONSENT_VERSION];
  if (!w) throw new Error(`CURRENT_CONSENT_VERSION ${CURRENT_CONSENT_VERSION} has no wording.`);
  return w;
}

/** The words a stored version stood for, or null for a version we never issued. */
export function consentWordingFor(version: string): ConsentWording | null {
  return Object.prototype.hasOwnProperty.call(CONSENT_WORDINGS, version) ? CONSENT_WORDINGS[version]! : null;
}

// ⚠️⚠️ KYCK'S WORDING IS A SEPARATE REGISTRY, ON PURPOSE (2026-10-11).
// A club's wording is a permission ASSERTION: "I have permission from everyone
// shown". Kyck cannot make that claim about a club's members and cannot know
// whether the club can — yet every staff upload used to be recorded as exactly
// that. Kyck's wording is an ACKNOWLEDGEMENT that Kyck supplied the file and the
// club remains responsible for permission. One registry holding both meanings is
// how the wrong words get shown to the wrong party, so they never share a map,
// and their version strings differ in form ('-v' vs '-k') so one cannot pass the
// other's check. Same rules as above: add a new entry, repoint the constant,
// leave old entries forever, and store the version the person was SHOWN.
//
// ⚠️⚠️ THE KYCK WORDING IS ALSO A PLACEHOLDER — Scott supplies the real text.
export const KYCK_CONSENT_WORDINGS: Readonly<Record<string, ConsentWording>> = {
  '2026-10-k1': {
    version: '2026-10-k1',
    text:
      'I am uploading these photos and videos on behalf of the club. Kyck does not hold ' +
      "the permission of the people shown; the club remains responsible for having permission to use them in marketing.",
    placeholder: true,
  },
};

export const CURRENT_KYCK_CONSENT_VERSION = '2026-10-k1';

export function currentKyckConsent(): ConsentWording {
  const w = KYCK_CONSENT_WORDINGS[CURRENT_KYCK_CONSENT_VERSION];
  if (!w) throw new Error(`CURRENT_KYCK_CONSENT_VERSION ${CURRENT_KYCK_CONSENT_VERSION} has no wording.`);
  return w;
}

/** The words a stored Kyck version stood for, or null for a version we never issued. */
export function kyckConsentWordingFor(version: string): ConsentWording | null {
  return Object.prototype.hasOwnProperty.call(KYCK_CONSENT_WORDINGS, version) ? KYCK_CONSENT_WORDINGS[version]! : null;
}

export type ConsentCheck = { ok: true; version: string } | { ok: false; reason: string };

/** The server's gate on an upload: the version the uploader was shown must be the current one. */
export function checkConsentVersion(shown: unknown): ConsentCheck {
  if (typeof shown !== 'string' || shown === '') {
    return { ok: false, reason: 'Please confirm you have permission to share these photos and videos.' };
  }
  if (shown !== CURRENT_CONSENT_VERSION) {
    return {
      ok: false,
      reason: 'The permission wording has changed since this page was opened. Reload the page and try again.',
    };
  }
  return { ok: true, version: shown };
}

export type UploadConsent =
  | { ok: true; kind: 'confirmedAtUpload' | 'uploadedByKyck'; version: string }
  | { ok: false; reason: string };

/**
 * ⚠️⚠️ THE CONSENT KIND IS DECIDED FROM WHO IS AUTHENTICATED, NEVER FROM THE
 * REQUEST. A client that could name its own kind could assert the club's
 * permission on Kyck's behalf — the whole problem inverted. The request carries
 * only the version it was shown, and that is checked against the registry of the
 * authenticated actor's own kind. A system actor uploads nothing and is refused.
 */
export function checkUploadConsent(actorKind: 'portalUser' | 'staff' | 'system', shown: unknown): UploadConsent {
  if (actorKind === 'portalUser') {
    const c = checkConsentVersion(shown);
    return c.ok ? { ok: true, kind: 'confirmedAtUpload', version: c.version } : c;
  }
  if (actorKind === 'staff') {
    if (typeof shown !== 'string' || shown === '') {
      return { ok: false, reason: 'Please confirm the upload acknowledgement.' };
    }
    if (shown !== CURRENT_KYCK_CONSENT_VERSION) {
      return {
        ok: false,
        reason: 'The upload acknowledgement has changed since this page was opened. Reload the page and try again.',
      };
    }
    return { ok: true, kind: 'uploadedByKyck', version: shown };
  }
  return { ok: false, reason: 'This actor cannot upload.' };
}
